import React, { useEffect, useState, useCallback } from "react";
import { Download, Share, Plus, X } from "lucide-react";

const SESSION_KEY = "jx-install-dismissed";

const isStandalone = () =>
  (typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true)) || false;

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const ios = /iphone|ipad|ipod/i.test(ua);
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return ios || iPadOS;
};

/**
 * "Add to Home Screen" prompt. Shows after login on every session until the
 * app is actually installed. Uses the native beforeinstallprompt flow on
 * Android/desktop Chrome, and a manual hint on iOS Safari (which has no API).
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(() =>
    typeof window !== "undefined" ? window.__deferredInstallPrompt || null : null
  );
  const [installed, setInstalled] = useState(isStandalone);
  const [dismissed, setDismissed] = useState(
    () => typeof sessionStorage !== "undefined" && sessionStorage.getItem(SESSION_KEY) === "1"
  );

  const ios = isIOS();

  useEffect(() => {
    const onInstallable = () => setDeferred(window.__deferredInstallPrompt || null);
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  }, []);

  const install = useCallback(async () => {
    const prompt = deferred || window.__deferredInstallPrompt;
    if (!prompt) return;
    prompt.prompt();
    try {
      const choice = await prompt.userChoice;
      if (choice?.outcome === "accepted") {
        setInstalled(true);
      } else {
        dismiss();
      }
    } finally {
      window.__deferredInstallPrompt = null;
      setDeferred(null);
    }
  }, [deferred, dismiss]);

  if (installed || dismissed) return null;
  // On iOS we always show the manual hint; elsewhere only when the browser
  // told us the app is installable.
  if (!ios && !deferred) return null;

  return (
    <div
      data-testid="install-prompt-banner"
      className="fixed inset-x-0 bottom-0 z-[9998] px-3 pb-3 sm:px-4 sm:pb-4 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto max-w-md surface-card bg-zinc-950/95 backdrop-blur border border-primary-accent/40 p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <img src="/icon-192.png" alt="Jinxing Sports" className="w-11 h-11 rounded-md flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-display tracking-wide uppercase text-sm text-white">Install Jinxing Sports</p>
            {ios ? (
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                Tap <Share size={12} className="inline -mt-0.5 text-primary-accent" /> then{" "}
                <span className="text-zinc-200">{"\u201CAdd to Home Screen\u201D"}</span>
                <Plus size={12} className="inline -mt-0.5 text-primary-accent" /> for the full app experience.
              </p>
            ) : (
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                Add it to your home screen for instant access and reliable match-day alerts.
              </p>
            )}
          </div>
          <button
            data-testid="install-prompt-dismiss"
            onClick={dismiss}
            aria-label="Dismiss"
            className="text-zinc-500 hover:text-white flex-shrink-0"
          >
            <X size={18} />
          </button>
        </div>
        {!ios && (
          <button
            data-testid="install-prompt-accept"
            onClick={install}
            className="mt-3 w-full bg-primary-accent text-white py-2.5 font-display tracking-widest text-xs uppercase flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
          >
            <Download size={14} /> Install App
          </button>
        )}
      </div>
    </div>
  );
}
